// Composes the screener's primary navigation, context, customization, and active filters.
import { SlidersHorizontal } from "lucide-react";
import type { ReactNode } from "react";
import { formatNumber } from "../lib/format";
import type { SavedViewRecord } from "../lib/saved-views-api";
import type {
  BuiltInViewId,
  ScreenerFilterState,
  ScreenerPageSize,
  ScreenerSortField,
  ScreenerState
} from "../lib/types";
import { ActiveFilterChips } from "./toolbar/ActiveFilterChips";
import { PaginationControls } from "./toolbar/PaginationControls";
import { ToolbarCustomize } from "./toolbar/ToolbarCustomize";
import { ViewSelector } from "./toolbar/ViewSelector";
import { Button } from "./ui/button";

interface TableToolbarProps {
  state: ScreenerState;
  pageIndex: number;
  totalRows: number;
  onFilterPatch: (filters: Partial<ScreenerFilterState>) => void;
  onPageSizeChange: (pageSize: ScreenerPageSize) => void;
  onPrevious: () => void;
  onNext: () => void;
  onOpenFilters: () => void;
  savedViews: readonly SavedViewRecord[];
  authenticated: boolean;
  accountAvailability: "loading" | "ready" | "error";
  savedViewMutationPending: boolean;
  onSignIn: () => void;
  onSelectBuiltInView: (id: BuiltInViewId) => void;
  onSelectSavedView: (view: SavedViewRecord) => void;
  onCreateSavedView: (name: string) => void;
  onRenameSavedView: (view: SavedViewRecord, name: string) => void;
  onDeleteSavedView: (view: SavedViewRecord) => void;
  tableControls?: ReactNode;
}

const SORT_LABELS: Record<ScreenerSortField, string> = {
  filingDate: "Filing date",
  tradeDate: "Trade date",
  ticker: "Ticker",
  companyName: "Company",
  insiderName: "Insider",
  value: "Value",
  shares: "Shares",
  price: "Price",
  ownershipChangePct: "Holdings change",
  materialityBps: "Materiality",
  filingLagDays: "Filing lag",
  score: "Score",
  confidence: "Confidence",
  distinctInsiderCount: "Cluster size"
};

/** Renders the complete screener toolbar without owning screener state. */
export function TableToolbar({
  state,
  pageIndex,
  totalRows,
  onFilterPatch,
  onPageSizeChange,
  onPrevious,
  onNext,
  onOpenFilters,
  savedViews,
  authenticated,
  accountAvailability,
  savedViewMutationPending,
  onSignIn,
  onSelectBuiltInView,
  onSelectSavedView,
  onCreateSavedView,
  onRenameSavedView,
  onDeleteSavedView,
  tableControls
}: TableToolbarProps) {
  const pageStart = totalRows === 0 ? 0 : pageIndex * state.pageSize + 1;
  const pageEnd = Math.min(totalRows, (pageIndex + 1) * state.pageSize);
  const pageCount = Math.max(1, Math.ceil(totalRows / state.pageSize));
  const selectedSavedView =
    state.savedViewId === null
      ? null
      : savedViews.find((view) => view.id === state.savedViewId) ?? null;
  const sortSummary = state.sorting
    .map((sort) => `${SORT_LABELS[sort.id]} ${sort.desc ? "descending" : "ascending"}`)
    .join(", ");

  return (
    <section
      aria-label="Table toolbar"
      className="sticky top-0 z-20 border-y border-border bg-surface"
    >
      <div className="flex min-h-10 flex-wrap items-center gap-2 px-2 py-1.5 text-xs">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 lg:hidden"
          aria-label="Open filters"
          onClick={onOpenFilters}
        >
          <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
          Filters
        </Button>

        <ViewSelector
          builtInViewId={state.builtInViewId}
          selectedSavedView={selectedSavedView}
          savedViews={savedViews}
          disabled={savedViewMutationPending}
          onSelectBuiltInView={onSelectBuiltInView}
          onSelectSavedView={onSelectSavedView}
        />

        {accountAvailability === "ready" && !authenticated ? (
          <button
            type="button"
            onClick={onSignIn}
            className="text-[11px] font-medium text-accent underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            Sign in to save views and use My watchlist.
          </button>
        ) : null}

        <span className="font-medium text-foreground">
          Showing {formatNumber(pageStart)}-{formatNumber(pageEnd)} of {formatNumber(totalRows)}
        </span>
        <span className="hidden text-muted-foreground md:inline">Sort: {sortSummary}</span>

        <div className="ml-auto flex items-center gap-1.5">
          <ToolbarCustomize
            pageSize={state.pageSize}
            onPageSizeChange={onPageSizeChange}
            selectedSavedView={selectedSavedView}
            authenticated={authenticated}
            accountAvailability={accountAvailability}
            savedViewMutationPending={savedViewMutationPending}
            onCreateSavedView={onCreateSavedView}
            onRenameSavedView={onRenameSavedView}
            onDeleteSavedView={onDeleteSavedView}
            tableControls={tableControls}
          />
          <PaginationControls
            pageIndex={pageIndex}
            pageCount={pageCount}
            onPrevious={onPrevious}
            onNext={onNext}
          />
        </div>
      </div>

      <ActiveFilterChips filters={state.filters} onFilterPatch={onFilterPatch} />
    </section>
  );
}
